/**
 * Image Zoom Handler (V2)
 * Orchestration/calculation only.
 */

import type { ImageZoomForm } from "./image-zoom-form";

export class ImageZoomHandler {
    public form: ImageZoomForm;

    public constructor(form: ImageZoomForm) {
        this.form = form;
    }

    public bootstrap(): void {
        this.resetOrigin();
    }

    public handlePointerMove(clientX: number, clientY: number): void {
        const root = this.form.root;
        const source = this.form.getSource();
        const target = this.form.getTarget();
        if (!(source instanceof HTMLElement) || !(target instanceof HTMLElement)) return;

        const rect = source.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;

        const x = Math.min(Math.max(clientX - rect.left, 0), rect.width);
        const y = Math.min(Math.max(clientY - rect.top, 0), rect.height);

        const xPercent = (x / rect.width) * 100;
        const yPercent = (y / rect.height) * 100;

        target.style.transformOrigin = `${xPercent}% ${yPercent}%`;

        this.syncPanelPosition(rect);

        if (
            root instanceof HTMLElement &&
            root.hasAttribute("data-image-zoom-popup-x") &&
            root.hasAttribute("data-image-zoom-popup-y")
        ) {
            const modalBodyRect = this.getModalBodyRect();
            const viewportWidth = Math.max(typeof globalThis.innerWidth === "number" ? globalThis.innerWidth : 0, 1);
            const viewportHeight = Math.max(typeof globalThis.innerHeight === "number" ? globalThis.innerHeight : 0, 1);

            root.dataset.imageZoomPopupX = clientX <= viewportWidth / 2 ? "right" : "left";
            root.dataset.imageZoomPopupY = modalBodyRect ? "top" : clientY <= viewportHeight / 2 ? "bottom" : "top";
        }
    }

    public resetOrigin(): void {
        const root = this.form.root;
        const target = this.form.getTarget();
        const panel = this.getPanel();
        if (!(target instanceof HTMLElement)) return;
        target.style.transformOrigin = "50% 50%";
        root.dataset.imageZoomActive = "0";

        if (panel instanceof HTMLElement) {
            panel.style.top = "";
            panel.style.bottom = "";
        }

        if (
            root instanceof HTMLElement &&
            root.hasAttribute("data-image-zoom-popup-x") &&
            root.hasAttribute("data-image-zoom-popup-y")
        ) {
            root.dataset.imageZoomPopupX = "right";
            root.dataset.imageZoomPopupY = "bottom";
        }
    }

    private getPanel(): HTMLElement | null {
        return this.form.getPanel();
    }

    private getModalBodyRect(): DOMRect | null {
        const modalBody = this.form.getModalBody();
        return modalBody instanceof HTMLElement ? modalBody.getBoundingClientRect() : null;
    }

    private getPanelHeight(panel: HTMLElement): number {
        const panelRect = panel.getBoundingClientRect();
        if (panelRect.height > 0) {
            return panelRect.height;
        }

        const computedHeight = Number.parseFloat(globalThis.getComputedStyle(panel).height || "0");
        return Number.isFinite(computedHeight) ? computedHeight : 0;
    }

    private syncPanelPosition(sourceRect: DOMRect): void {
        const panel = this.getPanel();
        const modalBodyRect = this.getModalBodyRect();

        if (!(panel instanceof HTMLElement)) {
            return;
        }

        if (!modalBodyRect) {
            panel.style.top = "";
            panel.style.bottom = "";
            return;
        }

        const panelHeight = this.getPanelHeight(panel);
        const edgePadding = 12;
        const minTop = modalBodyRect.top + edgePadding;
        const maxTop = Math.max(minTop, modalBodyRect.bottom - panelHeight - edgePadding);
        const nextTop = Math.min(Math.max(sourceRect.top, minTop), maxTop);

        panel.style.top = `${Math.round(nextTop)}px`;
        panel.style.bottom = "auto";
    }
}
